import { FC } from "react";
import { Box, Typography, IconButton, Checkbox } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { Colors } from "../../../../configs";
import {
  IBoxStorageHistoryInvoice,
  IBoxStorageHistoryRow,
} from "../../../../typings/interfaces/responses/boxStorageResponse";
import {
  formatCashierColumn,
  formatCurrency,
  formatDate,
} from "../../../../util/common-functions";
import BoxStorageInvoiceRow from "./box-storage-invoice-row";

export interface IBoxStorageHistoryTableRowProps {
  data: IBoxStorageHistoryRow;
  expanded: boolean;
  onToggleExpand(configId: number): void;
  selected: boolean;
  onToggleSelect(configId: number): void;
  onViewInvoice(invoice: IBoxStorageHistoryInvoice): void;
}

const getStatusInfo = (
  status: IBoxStorageHistoryRow["status"]
): { label: string; color: string } => {
  if (status === "IN_STORE") return { label: "In Store", color: Colors.MAIN_GRAY };
  if (status === "DUE") return { label: "Due", color: Colors.BROWN };
  return { label: "Picked Up", color: Colors.PRIMARY };
};

const BoxStorageHistoryTableRow: FC<IBoxStorageHistoryTableRowProps> = ({
  data,
  expanded,
  onToggleExpand,
  selected,
  onToggleSelect,
  onViewInvoice,
}) => {
  const statusInfo = getStatusInfo(data.status);
  const balanceColor = data.balance > 0 ? Colors.BROWN : Colors.MAIN_GRAY;
  const isFullyPickedUp = data.status === "PICKED_UP";

  const cellSx = {
    background: Colors.WHITE,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "1.14rem 0.28rem",
    boxSizing: "border-box",
    color: Colors.TEXT_GRAY_DARK,
    fontWeight: "bold",
    borderBottom: "1px solid #E8E8E8",
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: "0.15rem",
          flexWrap: "nowrap",
          marginBottom: "0.15rem",
          opacity: isFullyPickedUp ? 0.45 : 1,
        }}
      >
        <Box
          sx={{
            ...cellSx,
            margin: "0.01rem",
            flex: 1,
            minWidth: "2.92857142857rem",
            maxWidth: "2.92857142857rem !important",
            padding: "0.5rem 0.71rem",
          }}
        >
          <Checkbox
            size="small"
            checked={selected}
            onChange={() => onToggleSelect(data.configId)}
            sx={{
              "&.Mui-checked": { color: Colors.PRIMARY },
            }}
          />
        </Box>
        <Box
          sx={{
            ...cellSx,
            margin: "0.01rem",
            flex: 1,
            minWidth: "6.4rem",
          }}
        >
          <Typography sx={{ color: Colors.SECONDARY, fontWeight: "bold" }}>
            {data.boxNo}
          </Typography>
        </Box>
        <Box
          sx={{
            ...cellSx,
            margin: "0.01rem",
            flex: 1,
            minWidth: "6.8rem",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            {data.dateIn ? formatDate(data.dateIn) : "-"}
          </Typography>
        </Box>
        <Box
          sx={{
            ...cellSx,
            margin: "0.01rem",
            flex: 1,
            minWidth: "5.5rem",
          }}
        >
          <Typography sx={{ fontWeight: 400 }}>
            {formatCashierColumn(data.cashierName, null)}
          </Typography>
        </Box>
        <Box
          sx={{
            ...cellSx,
            margin: "0.01rem",
            flex: 1,
            minWidth: "6.5rem",
          }}
        >
          <Typography sx={{ fontWeight: 400 }}>
            {data.dueDate ? formatDate(data.dueDate) : "-"}
          </Typography>
        </Box>
        <Box
          sx={{
            ...cellSx,
            margin: "0.01rem",
            flex: 1,
            minWidth: "5.606rem",
          }}
        >
          <Typography>{data.pieces ?? "N/A"}</Typography>
        </Box>
        <Box
          sx={{
            ...cellSx,
            margin: "0.01rem",
            flex: 1,
            minWidth: "4.606rem",
          }}
        >
          <Typography sx={{ fontWeight: 400 }}>{data.rack || "-"}</Typography>
        </Box>
        <Box
          sx={{
            ...cellSx,
            margin: "0.01rem",
            flex: 1,
            minWidth: "5.506rem",
          }}
        >
          <Typography sx={{ color: balanceColor }}>
            {formatCurrency({ amount: data.balance, prefix: "$" })}
          </Typography>
        </Box>
        <Box
          sx={{
            ...cellSx,
            margin: "0.01rem",
            flex: 1,
            minWidth: "6.5rem",
          }}
        >
          <Typography sx={{ fontWeight: 400 }}>
            {data.payDate ? formatDate(data.payDate) : "-"}
          </Typography>
        </Box>
        <Box
          sx={{
            ...cellSx,
            margin: "0.01rem",
            flex: 1,
            minWidth: "6.5rem",
          }}
        >
          <Typography sx={{ fontWeight: 400 }}>
            {data.pickupDate ? formatDate(data.pickupDate) : "N/A"}
          </Typography>
        </Box>
        <Box
          sx={{
            ...cellSx,
            margin: "0.01rem",
            flex: 1,
            minWidth: "6.2rem",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              borderRadius: "999px",
              border: `1px solid ${statusInfo.color}40`,
              backgroundColor: `${statusInfo.color}1A`,
              padding: "0.21rem 0.64rem",
            }}
          >
            <Typography
              sx={{
                color: statusInfo.color,
                fontWeight: "bold",
                fontSize: "0.85rem",
                whiteSpace: "nowrap",
              }}
            >
              {statusInfo.label}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            ...cellSx,
            margin: "0.01rem",
            flex: 1.5,
            minWidth: "5.25rem",
            maxWidth: "5.25rem !important",
            padding: "0.36rem",
          }}
        >
          <IconButton
            size="small"
            aria-label={`Expand ${data.boxNo}`}
            onClick={() => onToggleExpand(data.configId)}
          >
            <KeyboardArrowDownIcon
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.15s ease",
                color: Colors.SECONDARY,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {expanded &&
        data.invoices.map((invoice, index) => (
          <BoxStorageInvoiceRow
            key={invoice.id}
            data={invoice}
            lastRow={index === data.invoices.length - 1}
            onViewClick={onViewInvoice}
          />
        ))}
    </>
  );
};

export default BoxStorageHistoryTableRow;
